import React, { useState, useContext, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import XDate from "xdate";
import Button from "Components/button/Button";
import { Icon, notFound } from "Icons/index";
import Select from "Components/select/Select";
import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import { displayFormattedDate } from "Utils/dateFormats";
import TranslatedString from "Components/TranslatedString";
import Loader from "Components/loader/Loader";
import DayPicker from "Components/dayPicker/DayPicker";
import Checkbox from "Components/checkbox/Checkbox";
import Tooltip from "Components/tooltip/Tooltip";
import { t } from "i18next";
import cx from "classnames";
import s from "./listAudits.scss";
import QboxmailApi from "@/qboxmailApi";
import { AuthContext, MediaQueriesContext } from "../../App";
import qs from "query-string";
import { AuditAutocompleteSelect } from "../../components/auditAutocompleteSelect/AuditAutocompleteSelect";
import { useApiQuery } from "../../hooks";
const emojiFlags = require("emoji-flags");

const api = new QboxmailApi({ useCookie: true });

const AuditsListScreen = () => {
  const location = useLocation();
  const history = useHistory();
  const { isLoggedUserAdmin, getLoggedUserType } = useContext(AuthContext);
  const { isMediaMobile } = useContext(MediaQueriesContext);

  const actionDropdownRef = useRef(null);
  const mediumDropdownRef = useRef(null);

  const parseDateString = (dateString) => {
    if (!dateString) return null;

    const parseDateComponent = (component) => {
      const parsedValue = parseInt(component, 10);
      return isNaN(parsedValue) ? null : parsedValue;
    };

    const [day, month, year] = dateString.split("-").map(parseDateComponent);

    if (!day || !month || !year) return null;

    const parsedDate = new Date(year, month - 1, day);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const composeInitialFilters = () => {
    const { query } = location;
    const currentDate = new XDate();

    const defaultDateRange = {
      from: currentDate.clone().addMonths(-12).toDate(),
      to: currentDate.clone().toDate(),
    };

    const formattedDateRange = {
      from: parseDateString(query.from) || defaultDateRange.from,
      to: parseDateString(query.to) || defaultDateRange.to,
    };

    return {
      ...formattedDateRange,
      action: query.action || "",
      view: query.view || "",
      who: query.who || "",
      target: query.target || "",
      medium: query.medium || "",
      include_audits_query: query.include_audits_query && query.include_audits_query !== "false",
    };
  };

  const [auditFilters, setAuditFilters] = useState(() => {
    const currentDate = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(currentDate.getFullYear() - 1);
    const hasNoDateRange = !location.query.from && !location.query.to;
    const initialFilters = hasNoDateRange
      ? { ...composeInitialFilters(), from: oneYearAgo, to: currentDate }
      : composeInitialFilters();

    return {
      from: null,
      to: null,
      action: "",
      view: "",
      who: "",
      ...initialFilters,
    };
  });
  const [expandedAuditEntries, setExpandedAuditEntries] = useState({});
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [shouldResetFilters, setShouldResetFilters] = useState(false);

  const { data: auditFormMetadata, isLoading: isFormMetadataLoading } = useApiQuery("getAuditFormInit", {
    onlyOnMount: true,
  });

  const {
    data: auditLogEntries,
    isLoading: isAuditLogsLoading,
    getData: fetchAuditLogEntries,
  } = useApiQuery("getAuditLogs", {
    enablePagination: true,
  });

  const { pagination: auditLogsPagination } = useApiQuery("getAuditLogsPagination", {
    enablePagination: true,
  });

  const fetchAuditResources = async (resourceType, searchQuery) => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : searchQuery;
    try {
      const response = await api.getAuditsResources(resourceType, trimmedQuery);
      return response?.resources || [];
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const handleFilterReset = async () => {
    try {
      const currentDate = new Date();
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(currentDate.getFullYear() - 1);

      setAuditFilters((previousFilters) => ({
        ...previousFilters,
        from: oneYearAgo,
        to: currentDate,
        action: "",
        view: "",
        who: "",
        target: "",
        medium: "",
        include_audits_query: false,
      }));

      if (actionDropdownRef.current) {
        actionDropdownRef.current.reset();
      }
      if (mediumDropdownRef.current) {
        mediumDropdownRef.current.reset();
      }

      setShouldResetFilters((previousState) => !previousState);

      history.push({
        pathname: "/audits",
        search: qs.stringify({
          page: 1,
          per: 25,
        }),
      });

      await fetchAuditLogEntries();
    } catch (error) {
      console.error("Error resetting filters:", error);
    }
  };

  const handleFilterSubmit = async (event) => {
    try {
      event.preventDefault();

      if (!auditFilters) return;

      const filterParameters = {
        page: 1,
        per: 25,
      };

      if (auditFilters.action) filterParameters.action = auditFilters.action;
      if (auditFilters.view) filterParameters.view = auditFilters.view;
      if (auditFilters.who) filterParameters.who = auditFilters.who;
      if (auditFilters.target) filterParameters.target = auditFilters.target;
      if (auditFilters.medium) filterParameters.medium = auditFilters.medium;
      if (auditFilters?.from) filterParameters.from = new XDate(auditFilters.from).toString("dd-MM-yyyy");
      if (auditFilters?.to) filterParameters.to = new XDate(auditFilters.to).toString("dd-MM-yyyy");
      if (auditFilters.include_audits_query) filterParameters.include_audits_query = auditFilters.include_audits_query;

      history.push({
        pathname: "/audits",
        search: qs.stringify(filterParameters),
      });

      setAuditFilters((previousFilters) => ({
        ...previousFilters,
        action: filterParameters.action || "",
        view: filterParameters.view || "",
        who: filterParameters.who || "",
        target: filterParameters.target || "",
        medium: filterParameters.medium || "",
        include_audits_query: filterParameters.include_audits_query || false,
        from: previousFilters.from,
        to: previousFilters.to,
      }));

      await fetchAuditLogEntries();
    } catch (error) {
      console.error("Error applying filters:", error);
    }
  };

  const validateDateRange = (from, to) => {
    if (!from || !to) return { from, to };

    try {
      const startDate = new XDate(from);
      const endDate = new XDate(to);

      if (startDate.diffDays(endDate) < 0) {
        return {
          from: startDate.clone().toDate(),
          to: startDate.clone().toDate(),
        };
      }
    } catch (error) {
      console.error("Error validating date range:", error);
    }

    return { from, to };
  };

  const updateFilterField = (fieldUpdate) => {
    try {
      setAuditFilters((previousFilters) => {
        const newFilters = {
          ...previousFilters,
          ...fieldUpdate,
        };

        if (fieldUpdate.from || fieldUpdate.to) {
          const { from, to } = validateDateRange(
            fieldUpdate.from || previousFilters.from,
            fieldUpdate.to || previousFilters.to
          );
          newFilters.from = from;
          newFilters.to = to;
        }

        return newFilters;
      });
    } catch (error) {
      console.error("Error updating filter field:", error);
    }
  };
  const toggleAuditEntryExpansion = (event) => {
    const auditEntryId = event.currentTarget.dataset.audid;
    setExpandedAuditEntries((previousState) => {
      if (previousState[auditEntryId] === undefined) {
        return {
          ...previousState,
          [auditEntryId]: true,
        };
      }
      return {
        ...previousState,
        [auditEntryId]: !previousState[auditEntryId],
      };
    });
  };

  const toggleFiltersPanel = () => {
    setIsFiltersPanelOpen((previousState) => !previousState);
  };

  const formatAuditActor = ({ who_medium, who_role, who_name, who_domain }) => {
    if (who_medium === "system") return "system";
    if (!isLoggedUserAdmin() && who_role === "admin") return "admin";
    return who_name + "@" + who_domain;
  };
  const availableActions = auditFormMetadata?.[0]?.actions.map((action) => ({
    value: action,
    label: action,
  }));

  return (
    <div
      className={cx(s.listAudits, {
        [s.showFilters]: isFiltersPanelOpen,
        [s.isAdmin]: isLoggedUserAdmin(),
        [s.isEmailAccount]: getLoggedUserType === "EmailAccount",
      })}
    >
      <div className={s.headerList}>
        <div className={cx("sectionTitle", s.titleList)}>{t("audits.list.header.title")}</div>

        {!isMediaMobile && (
          <div className={cx("sectionDesc", s.descList)}>
            <TranslatedString>audits.list.header.dsc</TranslatedString>
          </div>
        )}

        <div className={s.actionsList}>
          <Button docsKey="audits" color="docs" className={s.btnList} onlyIcon={isMediaMobile}>
            button.docs
          </Button>
        </div>
      </div>
      <form className={s.auditsFilters} method="post" onSubmit={handleFilterSubmit}>
        {isFiltersPanelOpen && isLoggedUserAdmin() && (
          <AuditAutocompleteSelect
            initValue={location.query.view}
            className={s.viewAs}
            name="view"
            label={t("input.label.audit.viewAs")}
            placeholder={t("input.placeholder.audit.viewAs")}
            onChange={(option) => updateFilterField({ view: option?.email })}
            triggerClear={shouldResetFilters}
            minLetters={4}
            loadOptions={(query) => fetchAuditResources("view", query)}
          />
        )}

        {isFiltersPanelOpen && (
          <DayPicker
            className={s.dayPickerFrom}
            label={t("input.label.date.from")}
            name="from"
            value={auditFilters?.from instanceof Date ? auditFilters.from : null}
            maxDate={new Date()}
            onChange={updateFilterField}
          />
        )}
        {isFiltersPanelOpen && (
          <DayPicker
            className={s.dayPickerTo}
            name="to"
            label={t("input.label.date.to")}
            value={auditFilters?.to instanceof Date ? auditFilters.to : null}
            minDate={auditFilters?.from instanceof Date ? auditFilters.from : null}
            maxDate={new Date()}
            onChange={updateFilterField}
          />
        )}

        {isFiltersPanelOpen && (
          <AuditAutocompleteSelect
            initValue={location.query.who}
            className={s.whoSelect}
            name="who"
            label={t("input.label.audit.who")}
            onChange={(option) => updateFilterField({ who: option?.code || "" })}
            triggerClear={shouldResetFilters}
            minLetters={4}
            loadOptions={(query) => fetchAuditResources("who", query)}
          />
        )}

        {isFiltersPanelOpen && (
          <Select
            className={s.actionSelect}
            components={{ DropdownIndicator }}
            label={t("input.label.audit.action")}
            name="action"
            options={availableActions}
            placeholder="Search"
            value={auditFilters?.action}
            onChange={(option) => updateFilterField({ action: option?.action || "" })}
            ref={(el) => (actionDropdownRef.current = el)}
            clearable
            isClearable={true}
            isSearchable={true}
          />
        )}

        {isFiltersPanelOpen && getLoggedUserType !== "EmailAccount" && (
          <AuditAutocompleteSelect
            initValue={location.query.target}
            name="view"
            className={s.subject}
            label={t("input.label.audit.target")}
            onChange={(option) => updateFilterField({ target: option?.code || "" })}
            triggerClear={shouldResetFilters}
            minLetters={4}
            loadOptions={(query) => fetchAuditResources("target", query)}
          />
        )}
        {isFiltersPanelOpen && (
          <Select
            className={s.medium}
            label={t("input.label.audit.medium")}
            components={{ DropdownIndicator }}
            name="medium"
            options={[
              { value: "panel", label: "panel" },
              { value: "vault", label: "vault" },
              { value: "system", label: "system" },
              { value: "api", label: "api" },
              { value: "webmail", label: "webmail" },
              { value: "mtm", label: "mail time machine" },
            ]}
            value={auditFilters?.medium || ""}
            onChange={updateFilterField}
            ref={(el) => (mediumDropdownRef.current = el)}
            clearable
            isClearable={true}
            isSearchable={true}
          />
        )}

        {isFiltersPanelOpen && (
          <Checkbox
            className={s.checbox}
            label={t("input.label.audit.includeQueryAudits")}
            name="include_audits_query"
            checked={auditFilters?.include_audits_query || false}
            onChange={updateFilterField}
          />
        )}
        {isFiltersPanelOpen && (
          <div className={s.wrapBtn}>
            <Button color="text" type="button" onClick={handleFilterReset}>
              button.reset
            </Button>
            <Button type="submit" loading={isFormMetadataLoading}>
              button.filter
            </Button>
          </div>
        )}
      </form>

      <div className={s.filters}>
        <div className={cx(s.showFilter, "internalLink")} onClick={toggleFiltersPanel}>
          {isFiltersPanelOpen ? t("button.advanced.filters.hide") : t("button.advanced.filters.show")}
        </div>

        <FullPaginationControls
          paginationInfo={auditLogsPagination}
          total={auditLogsPagination?.total || 0}
          location={location}
          refreshList={() => fetchAuditLogEntries()}
          showFilters={false}
          showListFilter={false}
          showPaginationFilter={false}
          isLoading={isAuditLogsLoading}
          shortenPagesOver={99}
        />
      </div>

      <div className={s.table}>
        <div className={s.headerTable}>
          <div className={s.dateHeaderTable}>{t("table.header.date")}</div>
          <div className={s.whoHeaderTable}>{t("table.header.who")}</div>
          <div className={s.contentHeaderTable}>{t("table.header.description")}</div>
        </div>
        {isAuditLogsLoading && (
          <div className={s.contentTable}>
            <div className={s.illustrationMsg}>
              <Loader iconSize="3rem" msg={t("loading.msg.data")} />
            </div>
          </div>
        )}

        {!isAuditLogsLoading && auditLogEntries?.length === 0 && (
          <div className={s.contentTable}>
            <div className={s.illustrationMsg}>
              <Icon svg={notFound} width="10rem" height="10rem" />
              <div className={s.msgIllustration}>{t("infoMsg.noRecords.msg")}</div>
              <div className={s.descIllustration}>{t("infoMsg.noRecords.desc")}</div>
            </div>
          </div>
        )}

        <div className={s.contentTable}>
          {!isAuditLogsLoading &&
            auditLogEntries?.map((auditEntry) => (
              <div className={s.wrapRowTable} key={auditEntry._id.$oid}>
                <div className={s.rowTable}>
                  <div className={cx(s.itemRow, s.dateRowTable)} data-label={t("table.header.date")}>
                    {displayFormattedDate(auditEntry.date)}
                  </div>
                  <div className={cx(s.itemRow, s.whoRowTable)} data-label={t("table.header.who")}>
                    {formatAuditActor(auditEntry)}
                  </div>
                  <div className={cx(s.itemRow, s.contentRowTable)} data-label={t("table.header.description")}>
                    {auditEntry.human || auditEntry.action}
                  </div>
                  <div className={cx(s.itemRow, s.flagRowTable)} data-label={t("table.header.country")}>
                    {auditEntry.who_country && emojiFlags[auditEntry.who_country] && (
                      <Tooltip
                        title={t("tooltip.country")}
                        text={emojiFlags[auditEntry.who_country].name}
                        containerStyle={{ float: "right" }}
                      >
                        <span className={s.flag}>{emojiFlags[auditEntry.who_country].emoji}</span>
                      </Tooltip>
                    )}
                  </div>

                  <div
                    className={cx(s.itemRow, s.showMoreRowTable)}
                    data-audid={auditEntry._id.$oid}
                    onClick={toggleAuditEntryExpansion}
                  >
                    {expandedAuditEntries[auditEntry._id.$oid] ? t("button.details.hide") : t("button.details.show")}
                  </div>
                </div>
                {expandedAuditEntries[auditEntry._id.$oid] && (
                  <div className={s.detailsRowTable}>
                    <pre>{JSON.stringify(auditEntry, null, 4)}</pre>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default AuditsListScreen;
